import { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import mixpanel from 'mixpanel-browser';
import { env } from '@/env';
import { useCurrentUserQuery } from '@/hooks/currentUser.generated';
import { isUserRetreivedAtom, userAtom } from '@/store/atoms';
import { datadogRum } from '@datadog/browser-rum';

export const UserContext = createContext<{
  user: any;
  onLogin: any;
  userRetreived: boolean;
}>({
  user: null,
  userRetreived: false,
  onLogin: () => true,
});

const useUserContext = () => useContext(UserContext);

function UserProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useAtom(userAtom);
  const [userRetreived, setUserRetreived] = useAtom(isUserRetreivedAtom);
  const [{ data, fetching }, refetchUser] = useCurrentUserQuery({
    pause: pathname === '/login',
  });

  if (data?.currentUser === null && userRetreived) {
    setUser(null);
  }
  // when mounting set the user state in the atom
  useEffect(() => {
    if (data?.currentUser?.id && data.currentUser?.id !== user?.id) {
      setUser({
        id: data.currentUser.id,
        familyName: data.currentUser.familyName,
        givenName: data.currentUser.givenName,
        phoneNumber: data.currentUser.phoneNumber,
        primaryAddress: data.currentUser.primaryAddress,
        addresses: data.currentUser.addresses,
      });
      datadogRum.setUser({
        id: data.currentUser.id,
        authId: data.currentUser.id,
        // email: data.currentUser.email,
      });
    }

    setUserRetreived(!fetching);
  }, [data, user, setUser, fetching, userRetreived, setUserRetreived]);

  const value = useMemo(() => {
    const onLogin = () => {
      if (env.VITE_MIXPANEL_TOKEN) {
        mixpanel.identify(user?.id);
      }
      const redirect = sessionStorage.getItem('redirect');
      refetchUser();
      if (redirect) {
        navigate(redirect, { replace: true });
      }
    };
    return { user, userRetreived, onLogin };
  }, [user, userRetreived, refetchUser, navigate]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export { useUserContext, UserProvider };
